import React, { useEffect, useState } from 'react';
import { Field, HelperMessage } from '@atlaskit/form';
import Textfield from '@atlaskit/textfield';
import Select from '@atlaskit/select';
import TaxIdInput from './MACBillingDetailsTaxIdInput';
import { Country } from 'modules/data/countries/model';
import WarningIcon from '@atlaskit/icon/glyph/warning';
import { styled } from 'styled-components';
import { Dimensions } from 'components/visuals';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { State } from 'model/State';
import { getTaxRate } from 'modules/data/taxRates/selectors';
import { colors } from '@atlaskit/theme';
import SectionMessage from '@atlaskit/section-message';
import config from 'appconfig';
import { PageH3Header } from './styledComponents';

const SelectWrapper = styled.div`
  width: ${Dimensions.units(50)}px;
`;

const filedLengthLimits = {
  address1: 50,
  address2: 100,
  city: 50,
  postcode: 11,
};

const WarningIconComponent = () => <WarningIcon label="Warning icon" primaryColor={colors.R500} />;

export const BillingDetailsFormSection = ({
  countryDetails,
  billingDetailsForm,
  fieldLengths,
  states,
  countryOptions,
  currencyChangeCountry,
  handleFormChange,
  handleFormStateSelectChange,
  handleFormCountrySelectChange,
  setBillingDetailsForm,
}) => {
  const taxRateForIsoAndState = useSelector((state: State) =>
    getTaxRate(state, {
      isoCountryCode: billingDetailsForm.country.value,
      stateKey: billingDetailsForm.state.value,
    }),
  ).ifSuccess((r) => r);
  const [taxIdLabel, setTaxIdLabel] = useState('');

  useEffect(() => {
    if (taxRateForIsoAndState) {
      setTaxIdLabel(
        taxRateForIsoAndState.taxIdLabel === 'ABN'
          ? 'Australian Business Number (ABN)'
          : taxRateForIsoAndState.taxIdLabel,
      );
    }
  }, [taxRateForIsoAndState]);

  return (
    <>
      <PageH3Header>
        <FormattedMessage
          defaultMessage="Billing Details"
          id="mac.payment-details.billing-details.header"
        />
      </PageH3Header>
      <p>
        <FormattedMessage
          defaultMessage="This will appear in your invoice"
          id="mac.payment-details.billing-details.description"
        />
      </p>
      <Field
        aria-required
        isRequired
        name="firstName"
        id="firstName"
        label={
          <FormattedMessage
            id="mac.payment-details.billing-form.first-name-input-label"
            defaultMessage="First Name"
          />
        }
      >
        {({ fieldProps }) => (
          <Textfield
            {...fieldProps}
            value={billingDetailsForm.firstName}
            onChange={(e) => handleFormChange(e, 'firstName')}
            data-cy="mac-billing-details-first-name-input"
            width={Dimensions.units(50)}
          />
        )}
      </Field>
      <Field
        aria-required
        isRequired
        name="lastName"
        id="lastName"
        label={
          <FormattedMessage
            id="mac.payment-details.billing-form.last-name-input-label"
            defaultMessage="Last Name"
          />
        }
      >
        {({ fieldProps }) => (
          <Textfield
            {...fieldProps}
            value={billingDetailsForm.lastName}
            onChange={(e) => handleFormChange(e, 'lastName')}
            data-cy="mac-billing-details-last-name-input"
            width={Dimensions.units(50)}
          />
        )}
      </Field>
      <Field
        aria-required
        isRequired
        name="companyName"
        id="companyName"
        label={
          <FormattedMessage
            id="mac.payment-details.billing-form.company-name-input-label"
            defaultMessage="Company Name"
          />
        }
      >
        {({ fieldProps }) => (
          <Textfield
            {...fieldProps}
            value={billingDetailsForm.companyName}
            onChange={(e) => handleFormChange(e, 'companyName')}
            data-cy="mac-billing-details-company-name-input"
            width={Dimensions.units(50)}
          />
        )}
      </Field>
      <Field
        aria-required
        isRequired
        name="address1"
        id="address1"
        label={
          <FormattedMessage
            id="mac.payment-details.billing-form.address-1-input-label"
            defaultMessage="Address 1"
          />
        }
      >
        {({ fieldProps }) => (
          <>
            <Textfield
              {...fieldProps}
              value={billingDetailsForm.address1}
              maxLength={50}
              onChange={(e) => handleFormChange(e, 'address1')}
              data-cy="mac-billing-details-address-1-input"
              width={Dimensions.units(50)}
            />
            {fieldLengths.address1 >= filedLengthLimits.address1 && (
              <HelperMessage>
                <FormattedMessage
                  defaultMessage="Max length for {field} is {length}"
                  id="mac.payment-details.billing-form.field-length-limit"
                  values={{
                    field: (
                      <FormattedMessage
                        id="mac.payment-details.billing-form.address-1-input-label"
                        defaultMessage="Address 1"
                      />
                    ),
                    length: filedLengthLimits.address1,
                  }}
                />
              </HelperMessage>
            )}
          </>
        )}
      </Field>
      <Field
        aria-required
        name="address2"
        id="address2"
        label={
          <FormattedMessage
            id="mac.payment-details.billing-form.address-2-input-label"
            defaultMessage="Address 2"
          />
        }
      >
        {({ fieldProps }) => (
          <>
            <Textfield
              {...fieldProps}
              maxLength={100}
              onChange={(e) => handleFormChange(e, 'address2')}
              value={billingDetailsForm.address2}
              data-cy="mac-billing-details-address-2-input"
              width={Dimensions.units(50)}
            />
            {fieldLengths.address2 >= filedLengthLimits.address2 && (
              <HelperMessage>
                <FormattedMessage
                  defaultMessage="Max length for {field} is {length}"
                  id="mac.payment-details.billing-form.field-length-limit"
                  values={{
                    field: (
                      <FormattedMessage
                        id="mac.payment-details.billing-form.address-2-input-label"
                        defaultMessage="Address 2"
                      />
                    ),
                    length: filedLengthLimits.address2,
                  }}
                />
              </HelperMessage>
            )}
          </>
        )}
      </Field>
      <Field
        aria-required
        isRequired
        name="city"
        id="city"
        label={
          <FormattedMessage
            id="mac.payment-details.billing-form.city-input-label"
            defaultMessage="City"
          />
        }
      >
        {({ fieldProps }) => (
          <>
            <Textfield
              {...fieldProps}
              value={billingDetailsForm.city}
              maxLength={50}
              onChange={(e) => handleFormChange(e, 'city')}
              data-cy="mac-billing-details-city-input"
              width={Dimensions.units(50)}
            />
            {fieldLengths.city >= filedLengthLimits.city && (
              <HelperMessage>
                <FormattedMessage
                  defaultMessage="Max length for {field} is {length}"
                  id="mac.payment-details.billing-form.field-length-limit"
                  values={{
                    field: (
                      <FormattedMessage
                        id="mac.payment-details.billing-form.city-input-label"
                        defaultMessage="City"
                      />
                    ),
                    length: filedLengthLimits.city,
                  }}
                />
              </HelperMessage>
            )}
          </>
        )}
      </Field>
      <Field
        aria-required
        isRequired
        name="state"
        id="state"
        label={
          <FormattedMessage
            id="mac.payment-details.billing-form.state-input-label"
            defaultMessage="State/Province"
          />
        }
      >
        {({ fieldProps }) => (
          <SelectWrapper data-cy="mac-billing-details-state-input">
            <Select
              {...fieldProps}
              hasAutocomplete
              options={states}
              onChange={handleFormStateSelectChange}
              value={billingDetailsForm.state || ''}
            />
          </SelectWrapper>
        )}
      </Field>
      <Field
        aria-required
        isRequired
        name="country"
        id="country"
        label={
          <FormattedMessage
            id="mac.payment-details.billing-form.country-input-label"
            defaultMessage="Country"
          />
        }
      >
        {({ fieldProps }) => (
          <SelectWrapper data-cy="mac-billing-details-country-input">
            <Select
              {...fieldProps}
              hasAutocomplete
              options={countryOptions}
              onChange={handleFormCountrySelectChange}
              value={billingDetailsForm.country || ''}
            />
          </SelectWrapper>
        )}
      </Field>
      <Field
        aria-required
        name="postcode"
        id="postcode"
        label={
          <FormattedMessage
            id="mac.payment-details.billing-form.postcode-input-label"
            defaultMessage="Postcode/ZIP"
          />
        }
      >
        {({ fieldProps }) => (
          <>
            <Textfield
              {...fieldProps}
              value={billingDetailsForm.postcode}
              maxLength={11}
              onChange={(e) => handleFormChange(e, 'postcode')}
              data-cy="mac-billing-details-postcode-input"
              width={Dimensions.units(50)}
            />
            {fieldLengths.postcode >= filedLengthLimits.postcode && (
              <HelperMessage>
                <FormattedMessage
                  defaultMessage="Max length for {field} is {length}"
                  id="mac.payment-details.billing-form.field-length-limit"
                  values={{
                    field: (
                      <FormattedMessage
                        id="mac.payment-details.billing-form.postcode-input-label"
                        defaultMessage="Postcode/ZIP"
                      />
                    ),
                    length: filedLengthLimits.postcode,
                  }}
                />
              </HelperMessage>
            )}
          </>
        )}
      </Field>
      <SelectWrapper>
        <TaxIdInput
          taxRate={taxRateForIsoAndState}
          showTaxId
          taxIdLabel={taxIdLabel}
          country={(countryDetails as unknown) as Country}
          vatResponsible={billingDetailsForm.vatResponsible}
          taxId={billingDetailsForm.taxId}
          handleFormChange={handleFormChange}
          setBillingDetailsForm={setBillingDetailsForm}
        />
      </SelectWrapper>
      {currencyChangeCountry && (
        <SectionMessage appearance="error" icon={WarningIconComponent}>
          <FormattedMessage
            defaultMessage="Can not set payment details. The change to {inputtedCountry} would result in a change of currency on your account. Please {contactCustomerSupport} to change your billing country."
            id="mac.payment-details.currency-change"
            values={{
              contactCustomerSupport: (
                <a href={config.companyContact}>
                  <FormattedMessage
                    defaultMessage="contact Customer Support"
                    id="mac.payment-details.contact-customer-support"
                  />
                </a>
              ),
              inputtedCountry: billingDetailsForm.country.label,
            }}
          />
        </SectionMessage>
      )}
    </>
  );
};
